/**
 * Same-company crew reassignment — move a live CEL#### / F ticket between
 * company pilots. Dispatch schedule stays master. Does not invent a pilot
 * who is not on the roster, and does not invent an unsafe board.
 *
 * Manual / Megan: named target, same tail, optional legal STD slip.
 * Decline handoff: Grok default, Classic fail-closed. Prefer Emerson ↔ Andrew
 * when both are company pilots and the board can absorb the trip.
 */
import {
  ASSIGNMENT_REASON_LABEL,
  addDeclineCooldown,
  appendAudit,
  assignmentForMission,
  assignmentLinkedMissionIds,
  canAssign,
  type AssignmentDeskSlice,
  type AssignmentReasonCode,
  type AssignmentSource,
  type CanAssignResult,
  type CrewCandidate,
  type TripAssignment,
} from "./assignment";
import { assignablePilots, crewById } from "./crew";
import { dutyViewFor, type DutyRoster } from "./duty";
import { displayFlightNumber } from "./flightNumber";
import { extractJsonPayload, XAI_CHAT_URL, XAI_INBOX_MODEL } from "./inbox";
import { proposeShiftedDeparture } from "./scheduleShift";
import { emptyAircraftState } from "./aircraftState";
import { icaosFromRoute, missingSceneryForCrew } from "./contentXmlSchedule";
import { hydrateCrewRoutePrefs, scoreCrewPrefMatch } from "./crewPrefs";
import type { AircraftSpec, CharterRequest, CrewMember, Mission, PilotProfile, SceneryAirport } from "./types";

export const REASSIGN_MISSING_SCENERY = "Target pilot is missing scenery for this route.";

export const REASSIGN_MISSING_CREW = "Target pilot is not on this company roster.";
export const REASSIGN_NOT_PILOT = "That crew member is not a company pilot.";
export const REASSIGN_NO_TRIP = "Trip is not on the board.";
export const REASSIGN_SAME_CREW = "Already on that pilot's trips.";
export const REASSIGN_NO_HANDOFF = "No company pilot can absorb this trip — decline stands.";

function newId(prefix: string, now: Date): string {
  return `${prefix}-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

export function normalizeCrewHint(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export function isSelfCrewHint(value: string): boolean {
  return /^(me|my|mine|myself|i)$/i.test(value.trim());
}

export type CrewHintRow = Pick<CrewMember, "id" | "displayName"> & Partial<Pick<CrewMember, "roles">>;

/** Company roster only — never invent a name that is not on file. */
export function resolveCompanyCrew(
  roster: readonly CrewHintRow[],
  hint: string,
  opts?: { callerCrewId?: string | null },
): CrewHintRow | null {
  const raw = hint.trim();
  if (!raw) return null;
  if (isSelfCrewHint(raw)) {
    const me = opts?.callerCrewId?.trim();
    if (!me) return null;
    return roster.find((row) => row.id === me) ?? null;
  }
  const byId = roster.find((row) => row.id === raw);
  if (byId) return byId;
  const norm = normalizeCrewHint(raw);
  if (!norm) return null;
  const exact = roster.find(
    (row) => normalizeCrewHint(row.id) === norm || normalizeCrewHint(row.displayName) === norm,
  );
  if (exact) return exact;
  const starts = roster.filter(
    (row) =>
      normalizeCrewHint(row.displayName).startsWith(norm) ||
      normalizeCrewHint(row.id).startsWith(norm) ||
      norm.startsWith(normalizeCrewHint(row.displayName)),
  );
  return starts.length === 1 ? starts[0]! : null;
}

export function companyPilotsForReassign(roster: CrewMember[]): CrewMember[] {
  return assignablePilots(roster);
}

/** Emerson declines → prefer Andrew; Andrew declines → prefer Emerson; else name order. */
export function preferHandoffOrder(decliningCrewId: string, pilots: CrewMember[]): CrewMember[] {
  const rest = pilots.filter((p) => p.id !== decliningCrewId);
  const declining = pilots.find((p) => p.id === decliningCrewId);
  const name = (declining?.displayName || declining?.id || "").toLowerCase();
  const prefer =
    name.includes("emerson") ? "andrew" : name.includes("andrew") ? "emerson" : "";
  return rest.slice().sort((a, b) => {
    const aPref = prefer && a.displayName.toLowerCase().includes(prefer) ? 0 : 1;
    const bPref = prefer && b.displayName.toLowerCase().includes(prefer) ? 0 : 1;
    if (aPref !== bPref) return aPref - bPref;
    return a.displayName.localeCompare(b.displayName);
  });
}

export function requestFromBoardTrip(mission: Mission, inbox: CharterRequest[] = []): CharterRequest {
  const existing = mission.inboxRequestId
    ? inbox.find((row) => row.id === mission.inboxRequestId)
    : undefined;
  if (existing) {
    return {
      ...existing,
      originIcao: mission.originIcao,
      destIcao: mission.destIcao,
      originName: mission.originName || existing.originName,
      destName: mission.destName || existing.destName,
      departureIso: mission.departureIso,
      paxCount: mission.paxCount,
      cargoLb: mission.cargoLb ?? existing.cargoLb,
      suggestedAircraftId: mission.aircraftId || existing.suggestedAircraftId,
    };
  }
  return {
    id: mission.inboxRequestId || `board-${mission.id}`,
    createdAt: mission.createdAt,
    source: "automatic",
    status: "assigned",
    clientName: mission.purpose || "Company",
    clientEmail: "",
    clientCompany: "",
    originIcao: mission.originIcao,
    destIcao: mission.destIcao,
    originName: mission.originName,
    destName: mission.destName,
    departureIso: mission.departureIso,
    paxCount: mission.paxCount,
    cargoLb: mission.cargoLb ?? 0,
    reason: mission.purpose || "",
    specialRequests: "",
    emailSubject: "",
    emailBody: "",
    suggestedAircraftId: mission.aircraftId,
    flightType: mission.flightType,
    mxPositioning: mission.mxPositioning,
  };
}

function linkedIdsFor(mission: Mission, assignments: TripAssignment[]): Set<string> {
  const ids = new Set<string>([mission.id]);
  const open = assignmentForMission(assignments, mission.id);
  if (open) {
    for (const id of assignmentLinkedMissionIds(open)) ids.add(id);
  }
  return ids;
}

export function boardForReassignScore(
  mission: Mission,
  missions: Mission[],
  assignments: TripAssignment[],
): { missions: Mission[]; assignments: TripAssignment[] } {
  const ignore = linkedIdsFor(mission, assignments);
  const current = assignmentForMission(assignments, mission.id);
  return {
    missions: missions.filter((m) => !ignore.has(m.id)),
    assignments: assignments.filter((a) => a.id !== current?.id && !a.superseded),
  };
}

export function crewCandidateFor(
  member: CrewMember,
  duty: DutyRoster,
  profile: PilotProfile,
  now: Date,
): CrewCandidate {
  return {
    crewId: member.id,
    crewName: member.displayName,
    ratedIcaoTypes: member.ratedIcaoTypes.length ? member.ratedIcaoTypes : profile.ratedIcaoTypes,
    duty: dutyViewFor(duty, { id: member.id, name: member.displayName }, now),
    routeAreaPrefs: member.routeAreaPrefs,
    preferredTripNm: member.preferredTripNm,
  };
}

export type ReassignFit = {
  ok: true;
  result: CanAssignResult;
  request: CharterRequest;
  shifted: boolean;
  departureIso: string;
  warning?: string;
};

export type ReassignFitFail = { ok: false; error: string };

/** Same tail, existing realism rules. May slip STD when overlap / reposition is the only block. */
export function scoreCrewReassign(input: {
  mission: Mission;
  target: CrewCandidate;
  aircraft: AircraftSpec;
  missions: Mission[];
  assignments: TripAssignment[];
  inbox: CharterRequest[];
  scenery: SceneryAirport[];
  now?: Date;
  allowShift?: boolean;
}): ReassignFit | ReassignFitFail {
  const now = input.now ?? new Date();
  const request = requestFromBoardTrip(input.mission, input.inbox);
  const board = boardForReassignScore(input.mission, input.missions, input.assignments);
  const rest = {
    request,
    aircraft: input.aircraft,
    aircraftState: {
      ...emptyAircraftState(now),
      parkedIcao: input.aircraft.lastAirportIcao?.toUpperCase() ?? null,
      currentIcao: input.aircraft.lastAirportIcao?.toUpperCase() ?? null,
    },
    missions: board.missions,
    assignments: board.assignments,
    duty: input.target.duty,
    scenery: input.scenery,
    crewId: input.target.crewId,
    crewName: input.target.crewName,
    ratedIcaoTypes: input.target.ratedIcaoTypes,
    now,
  };
  const direct = canAssign(rest);
  const blocking = direct.hard.filter((h) => h.code !== "already_assigned" && h.code !== "already_crewed");
  if (blocking.length === 0 && direct.ok) {
    return { ok: true, result: direct, request, shifted: false, departureIso: request.departureIso };
  }
  if (input.allowShift === false) {
    return { ok: false, error: blocking[0]?.message || "Target pilot cannot take this trip." };
  }
  const shifted = proposeShiftedDeparture({
    request,
    aircraft: input.aircraft,
    missions: board.missions,
    assignments: board.assignments,
    scenery: input.scenery,
    crewId: input.target.crewId,
    now,
    currentIcao: direct.currentIcao ?? input.aircraft.lastAirportIcao,
  });
  if (!shifted.ok) {
    return { ok: false, error: blocking[0]?.message || shifted.error || "Target pilot cannot take this trip." };
  }
  const nextReq = { ...request, departureIso: shifted.departureIso };
  const retry = canAssign({ ...rest, request: nextReq });
  const retryBlock = retry.hard.filter((h) => h.code !== "already_assigned" && h.code !== "already_crewed");
  if (!retry.ok || retryBlock.length) {
    return { ok: false, error: retryBlock[0]?.message || "Target pilot cannot take this trip even with a slipped STD." };
  }
  return {
    ok: true,
    result: retry,
    request: nextReq,
    shifted: shifted.shifted,
    departureIso: shifted.departureIso,
    warning: shifted.warning,
  };
}

function slipMissionStd(mission: Mission, departureIso: string): Mission {
  const legs = mission.legs?.length
    ? mission.legs.map((leg, i) => (i === 0 ? { ...leg, departureIso } : leg))
    : mission.legs;
  return { ...mission, departureIso, legs };
}

export type ExecuteReassignInput = {
  slice: AssignmentDeskSlice;
  mission: Mission;
  target: { crewId: string; crewName: string };
  source: AssignmentSource;
  now?: Date;
  /** When the caller is taking the trip themselves (Megan "on my trips"). */
  accept?: boolean;
  departureIso?: string;
  fit?: CanAssignResult;
  note?: string;
};

export function executeReassignCrew(
  input: ExecuteReassignInput,
): { ok: true; slice: AssignmentDeskSlice; assignment: TripAssignment; notice: string } | { ok: false; error: string } {
  const now = input.now ?? new Date();
  const mission = input.slice.missions.find((m) => m.id === input.mission.id);
  if (!mission || mission.status === "cancelled" || mission.status === "completed") {
    return { ok: false, error: REASSIGN_NO_TRIP };
  }
  const existing = assignmentForMission(input.slice.assignments, mission.id);
  if (existing && existing.crewId === input.target.crewId) {
    return { ok: false, error: REASSIGN_SAME_CREW };
  }
  const departureIso = input.departureIso ?? mission.departureIso;
  const slipped = departureIso !== mission.departureIso;
  const linked = existing ? assignmentLinkedMissionIds(existing) : [mission.id];
  const missions = input.slice.missions.map((m) => {
    if (!linked.includes(m.id) && m.id !== mission.id) return m;
    return slipped && m.id === mission.id ? slipMissionStd(m, departureIso) : m;
  });
  let assignments = input.slice.assignments;
  let audit = input.slice.assignmentAudit;
  if (existing) {
    assignments = assignments.map((a) =>
      a.id === existing.id
        ? { ...a, superseded: true, status: a.status === "accepted" ? a.status : "declined" }
        : a,
    );
    audit = appendAudit(
      audit,
      {
        actor: input.source,
        action: "supersede",
        requestId: existing.requestId,
        assignmentId: existing.id,
        aircraftId: existing.aircraftId,
        note: `Reassign ${displayFlightNumber(mission)} ${existing.crewName || existing.crewId} → ${input.target.crewName}.`,
      },
      now,
    );
  }
  const assignment: TripAssignment = {
    id: newId("asgn", now),
    requestId: existing?.requestId || mission.inboxRequestId || `board-${mission.id}`,
    missionId: existing?.missionId || mission.id,
    ferryMissionId: existing?.ferryMissionId,
    returnFerryMissionId: existing?.returnFerryMissionId,
    aircraftId: existing?.aircraftId || mission.aircraftId,
    registration: existing?.registration || mission.registration,
    crewId: input.target.crewId,
    crewName: input.target.crewName,
    status: input.accept ? "accepted" : "pending",
    source: input.source,
    assignedAtUtc: now.toISOString(),
    decidedAtUtc: input.accept ? now.toISOString() : undefined,
    comment: input.note,
    repositionRequired: input.fit?.repositionRequired ?? existing?.repositionRequired ?? false,
    repositionNm: input.fit?.repositionNm ?? existing?.repositionNm,
    repositionEteMin: input.fit?.repositionEteMin ?? existing?.repositionEteMin,
  };
  audit = appendAudit(
    audit,
    {
      actor: input.source,
      action: existing ? "reassign" : "assign",
      requestId: assignment.requestId,
      assignmentId: assignment.id,
      aircraftId: assignment.aircraftId,
      note:
        input.note ||
        `${input.source} reassigned ${displayFlightNumber(mission)} to ${input.target.crewName}${
          slipped ? ` · STD ${departureIso}` : ""
        }.`,
    },
    now,
  );
  const inbox = mission.inboxRequestId
    ? input.slice.inbox.map((row) =>
        row.id === mission.inboxRequestId
          ? { ...row, status: (assignment.status === "accepted" ? "accepted" : "assigned") as CharterRequest["status"], acceptedMissionId: mission.id }
          : row,
      )
    : input.slice.inbox;
  const fromName = existing?.crewName || existing?.crewId || "unassigned";
  return {
    ok: true,
    slice: {
      inbox,
      missions,
      assignments: [assignment, ...assignments.filter((a) => a.id !== assignment.id)],
      assignmentAudit: audit,
      declineCooldowns: input.slice.declineCooldowns,
    },
    assignment,
    notice: `REASSIGNED  ${displayFlightNumber(mission)}  ${fromName} → ${input.target.crewName}${
      slipped ? ` · STD ${departureIso}` : ""
    }`,
  };
}

export type DeclineHandoffPick = {
  target: CrewCandidate;
  member: CrewMember;
  fit: ReassignFit;
};

export function pickClassicDeclineHandoff(input: {
  assignment: TripAssignment;
  mission: Mission;
  roster: CrewMember[];
  duty: DutyRoster;
  profile: PilotProfile;
  fleet: AircraftSpec[];
  missions: Mission[];
  assignments: TripAssignment[];
  inbox: CharterRequest[];
  scenery: SceneryAirport[];
  now?: Date;
  scheduleOnlyContentXml?: boolean;
}): DeclineHandoffPick | null {
  const now = input.now ?? new Date();
  const aircraft = input.fleet.find((a) => a.id === input.mission.aircraftId);
  if (!aircraft) return null;
  const routeIcaos = icaosFromRoute(input.mission);
  const pilots = preferHandoffOrder(input.assignment.crewId, companyPilotsForReassign(input.roster));
  let best: DeclineHandoffPick | null = null;
  let bestScore = Number.NEGATIVE_INFINITY;
  for (const member of pilots) {
    if (input.scheduleOnlyContentXml && missingSceneryForCrew(member, routeIcaos).length) continue;
    const target = crewCandidateFor(member, input.duty, input.profile, now);
    const fit = scoreCrewReassign({
      mission: input.mission,
      target,
      aircraft,
      missions: input.missions,
      assignments: input.assignments,
      inbox: input.inbox,
      scenery: input.scenery,
      now,
      allowShift: true,
    });
    if (!fit.ok) continue;
    const pref = scoreCrewPrefMatch(hydrateCrewRoutePrefs(member), {
      originIcao: input.mission.originIcao,
      destIcao: input.mission.destIcao,
      destName: input.mission.destName,
      scenery: input.scenery,
    });
    const score = (fit.result.score ?? 0) + pref;
    if (!best || score > bestScore) {
      best = { target, member, fit };
      bestScore = score;
    }
  }
  return best;
}

export function executeDeclineHandoff(input: {
  slice: AssignmentDeskSlice;
  assignment: TripAssignment;
  mission: Mission;
  pick: DeclineHandoffPick;
  reasonCode: AssignmentReasonCode;
  declineReasonId?: string;
  comment?: string;
  actor?: AssignmentSource | "pilot";
  now?: Date;
}): { ok: true; slice: AssignmentDeskSlice; assignment: TripAssignment; notice: string } {
  const now = input.now ?? new Date();
  const actor = input.actor === "voice" || input.actor === "dispatcher" || input.actor === "auto-agent" ? input.actor : "pilot";
  const comment = (input.comment ?? "").trim();
  let audit = appendAudit(
    input.slice.assignmentAudit,
    {
      actor,
      action: "decline",
      requestId: input.assignment.requestId,
      assignmentId: input.assignment.id,
      aircraftId: input.assignment.aircraftId,
      note: `${ASSIGNMENT_REASON_LABEL[input.reasonCode]}${comment ? ` — ${comment}` : ""} · handoff to ${input.pick.target.crewName}`,
    },
    now,
  );
  const declined: TripAssignment = {
    ...input.assignment,
    status: "declined",
    superseded: true,
    reasonCode: input.reasonCode,
    declineReasonId: input.declineReasonId || input.assignment.declineReasonId,
    comment: comment || undefined,
    decidedAtUtc: now.toISOString(),
  };
  const working: AssignmentDeskSlice = {
    ...input.slice,
    assignments: input.slice.assignments.map((a) => (a.id === declined.id ? declined : a)),
    assignmentAudit: audit,
    declineCooldowns: addDeclineCooldown(input.slice.declineCooldowns, input.assignment.requestId, input.assignment.crewId, now),
  };
  const applied = executeReassignCrew({
    slice: working,
    mission: input.mission,
    target: input.pick.target,
    source: "auto-agent",
    now,
    accept: false,
    departureIso: input.pick.fit.departureIso,
    fit: input.pick.fit.result,
    note: `Decline handoff after ${input.assignment.crewName || input.assignment.crewId}`,
  });
  if (!applied.ok) {
    return {
      ok: true,
      slice: working,
      assignment: declined,
      notice: `DECLINED  ${displayFlightNumber(input.mission)}  ${REASSIGN_NO_HANDOFF}`,
    };
  }
  return {
    ok: true,
    slice: applied.slice,
    assignment: applied.assignment,
    notice: `HANDOFF  ${displayFlightNumber(input.mission)}  ${input.assignment.crewName || input.assignment.crewId} → ${input.pick.target.crewName}${
      input.pick.fit.shifted ? ` · ${input.pick.fit.warning || "STD slipped"}` : ""
    }`,
  };
}

export type GrokHandoffAsk = {
  crewId?: string | null;
  slipStd?: string | null;
  none?: boolean;
  reason?: string;
};

export async function askGrokDeclineHandoff(input: {
  apiKey: string;
  inboxModel?: string;
  mission: Mission;
  declining: { crewId: string; crewName: string };
  pilots: CrewMember[];
  fetchImpl?: typeof fetch;
}): Promise<{ ok: true; crewId: string } | { ok: false; reason: string }> {
  const key = input.apiKey.trim();
  if (!key) return { ok: false, reason: "No xAI key — Classic safety net is scheduling." };
  const fetchImpl = input.fetchImpl ?? fetch;
  const pilots = input.pilots
    .filter((p) => p.id !== input.declining.crewId)
    .map((p) => ({ id: p.id, name: p.displayName }));
  if (!pilots.length) return { ok: false, reason: "No other company pilot on the roster." };
  try {
    const res = await fetchImpl(XAI_CHAT_URL, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${key}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: input.inboxModel?.trim() || XAI_INBOX_MODEL,
        temperature: 0.1,
        response_format: { type: "json_object" },
        messages: [
          {
            role: "system",
            content:
              "You are the Celtic Air company dispatcher. Pick one company pilot id who can take a declined trip, or none. Desk-snapshot only. Never invent a crew id. Return JSON {\"crewId\":\"id-or-null\",\"none\":true|false}.",
          },
          {
            role: "user",
            content: JSON.stringify({
              trip: {
                id: input.mission.id,
                flightNumber: displayFlightNumber(input.mission),
                route: `${input.mission.originIcao}-${input.mission.destIcao}`,
                std: input.mission.departureIso,
                tail: input.mission.registration,
              },
              declining: input.declining,
              companyPilots: pilots,
            }),
          },
        ],
      }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      return { ok: false, reason: `Grok handoff HTTP ${res.status}${text ? `: ${text.slice(0, 120)}` : ""}` };
    }
    const payload = (await res.json()) as {
      choices?: { message?: { content?: string } }[];
      error?: { message?: string };
    };
    if (payload.error?.message) return { ok: false, reason: payload.error.message };
    const raw = payload.choices?.[0]?.message?.content;
    if (!raw) return { ok: false, reason: "Grok returned an empty handoff." };
    const parsed = (typeof raw === "string" ? extractJsonPayload(raw) : raw) as GrokHandoffAsk;
    const crewId = typeof parsed?.crewId === "string" ? parsed.crewId.trim() : "";
    if (!crewId || parsed?.none) return { ok: false, reason: parsed?.reason?.trim() || "Grok found no company pilot fit." };
    if (!pilots.some((p) => p.id === crewId)) {
      return { ok: false, reason: "Grok named a pilot who is not on this company roster." };
    }
    return { ok: true, crewId };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Grok handoff failed.";
    return { ok: false, reason: message };
  }
}

export function resolveReassignTarget(input: {
  roster: CrewMember[];
  hint: string;
  callerCrewId?: string | null;
}): { ok: true; member: CrewMember } | { ok: false; error: string } {
  const raw = input.hint.trim();
  if (!raw) return { ok: false, error: REASSIGN_MISSING_CREW };
  const hit = resolveCompanyCrew(input.roster, raw, { callerCrewId: input.callerCrewId });
  if (!hit) {
    return {
      ok: false,
      error: isSelfCrewHint(raw)
        ? "Caller is not on this company roster."
        : `No crew named ${raw} on this company roster.`,
    };
  }
  const member = crewById(input.roster, hit.id);
  if (!member) return { ok: false, error: REASSIGN_MISSING_CREW };
  if (!member.roles.includes("pilot")) return { ok: false, error: `${member.displayName} is not a company pilot.` };
  return { ok: true, member };
}
